/**
 * NotificationManager Component
 * Manages transient toast notifications for insights, status updates, and alerts
 *
 * Responsibilities:
 * - Display toast notifications
 * - Auto-dismiss with configurable duration
 * - Queue multiple notifications
 * - Handle user dismissal
 */

export class NotificationManager {
  /**
   * Toast type → sprite symbol id.
   *
   * The `icon-toast-*` set exists specifically for this component: one 24x24
   * geometry, one 1.6 stroke weight, one ring radius. It is deliberately NOT
   * `icon-info` / `icon-checkmark` / `icon-lightbulb` — those serve other call
   * sites (index.html's help affordance, seven checkmark consumers) at their
   * own weights, and restyling them for the toast would have changed all of
   * them. Unknown types fall back to the informational glyph.
   */
  static ICONS = {
    info: 'icon-toast-info',
    success: 'icon-toast-success',
    error: 'icon-toast-error',
    insight: 'icon-toast-insight'
  };

  /**
   * @param {HTMLElement} containerElement - Container element for notifications
   */
  constructor(containerElement) {
    this.container = containerElement;
    this.notifications = new Map(); // Map of id -> notification element
    this.nextId = 1;
  }

  /**
   * Show an info notification
   * @param {string} message - Notification message
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {Object} [options] - See {@link NotificationManager#show}
   * @returns {number} Notification ID
   */
  showInfo(message, duration = 4000, options = {}) {
    return this.show(message, 'info', duration, options);
  }

  /**
   * Show a success notification
   * @param {string} message - Notification message
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {Object} [options] - See {@link NotificationManager#show}
   * @returns {number} Notification ID
   */
  showSuccess(message, duration = 4000, options = {}) {
    return this.show(message, 'success', duration, options);
  }

  /**
   * Show an error notification
   * @param {string} message - Notification message
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {Object} [options] - See {@link NotificationManager#show}
   * @returns {number} Notification ID
   */
  showError(message, duration = 6000, options = {}) {
    return this.show(message, 'error', duration, options);
  }

  /**
   * Show an insight notification.
   *
   * The category becomes the message line and the insight text the description,
   * so the two-line layout does the work a "💡 Category: text" prefix used to.
   *
   * @param {Object} insightData - Insight data object
   * @param {string} insightData.category - Insight category
   * @param {string} insightData.description - Insight description
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @returns {number} Notification ID
   */
  showInsight(insightData, duration = 8000) {
    const text = insightData.description || 'New insight detected';
    const category = insightData.category;

    return category
      ? this.show(category, 'insight', duration, { description: text })
      : this.show(text, 'insight', duration);
  }

  /**
   * Show a notification
   * @param {string} message - Notification message (the first, emphasised line
   *   when a description is supplied)
   * @param {string} type - Notification type (info, success, error, insight)
   * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
   * @param {Object} [options] - Extra content
   * @param {string} [options.description] - Optional second line. Use where a
   *   message genuinely splits into what happened + which item; leave unset for
   *   the one-clause messages that make up most of the app.
   * @returns {number} Notification ID
   */
  show(message, type = 'info', duration = 4000, { description = '' } = {}) {
    const id = this.nextId++;

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification-toast__item notification-toast__item--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('data-notification-id', id);

    // Status icon. Carries the type as SHAPE, so the signal survives colour-
    // vision deficiency and greyscale — the tinted card edge carries the colour.
    // Purely decorative: the text carries the meaning, so it is hidden from
    // assistive tech.
    const symbol = NotificationManager.ICONS[type] || NotificationManager.ICONS.info;
    const icon = document.createElement('span');
    icon.className = 'notification-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `<svg viewBox="0 0 24 24"><use href="#${symbol}"/></svg>`;

    // Text column
    const body = document.createElement('div');
    body.className = 'notification-toast__body';

    const messageDiv = document.createElement('div');
    messageDiv.className = 'notification-toast__message';
    messageDiv.textContent = message;
    body.appendChild(messageDiv);

    // Optional second line. Only when supplied — a lone message must not be
    // bolded, so the title weight rides on the description's presence.
    if (description) {
      messageDiv.classList.add('notification-toast__message--titled');
      const descriptionDiv = document.createElement('div');
      descriptionDiv.className = 'notification-toast__description';
      descriptionDiv.textContent = description;
      body.appendChild(descriptionDiv);
    }

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'notification-toast__close';
    closeButton.setAttribute('aria-label', 'Close notification');
    closeButton.innerHTML = '<svg width="20" height="20"><use href="#icon-close"/></svg>';
    closeButton.addEventListener('click', () => this.dismiss(id));

    // Assemble notification
    notification.appendChild(icon);
    notification.appendChild(body);
    notification.appendChild(closeButton);

    // Add to container
    this.container.appendChild(notification);

    // Store reference
    this.notifications.set(id, notification);

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      notification.classList.add('notification-toast__item--visible');
    });

    // Auto-dismiss after duration
    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }

    return id;
  }

  /**
   * Dismiss a notification by ID
   * @param {number} id - Notification ID
   */
  dismiss(id) {
    const notification = this.notifications.get(id);
    if (!notification) return;

    // Trigger slide-out animation
    notification.classList.remove('notification-toast__item--visible');
    notification.classList.add('notification-toast__item--hiding');

    // Remove from DOM after animation
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      this.notifications.delete(id);
    }, 300); // Match CSS transition duration
  }

  /**
   * Dismiss all notifications
   */
  clear() {
    const ids = Array.from(this.notifications.keys());
    ids.forEach(id => this.dismiss(id));
  }

  /**
   * Get count of active notifications
   * @returns {number}
   */
  getCount() {
    return this.notifications.size;
  }
}
